import React, { useState, useRef, useEffect } from 'react';
import apiService from '../services/apiService';

/**
 * AISpecialistMatcher — 3-step guided UI
 *
 * Props:
 *   specialists      — full array of specialist objects from the API
 *   onMatchResults   — callback({ matchedIds: string[], summary: string, reasoningMap })
 *   onClear          — callback() to clear AI filter
 *   isActive         — bool, whether an AI filter is currently applied
 */

const SYMPTOM_OPTIONS = [
  { value: 'respiratory',    label: 'Respiratory issues' },
  { value: 'digestive',      label: 'Digestive problems' },
  { value: 'low_production', label: 'Low egg production' },
  { value: 'lethargy',       label: 'Lethargy / low activity' },
  { value: 'skin_feathers',  label: 'Skin / feather issues' },
  { value: 'sudden_death',   label: 'Sudden deaths' },
  { value: 'vaccination',    label: 'Vaccination needed' },
  { value: 'nutrition',      label: 'Nutrition / feed advice' },
  { value: 'biosecurity',    label: 'Biosecurity audit' },
  { value: 'other',          label: 'Other (describe below)' },
];

const FLOCK_OPTIONS = [
  { value: 'broiler',     label: 'Broilers' },
  { value: 'layer',       label: 'Layers' },
  { value: 'breeder',     label: 'Breeders' },
  { value: 'turkey',      label: 'Turkeys' },
  { value: 'free_range',  label: 'Free-range' },
  { value: 'mixed',       label: 'Mixed' },
];

const GENDER_OPTIONS = [
  { value: 'any',    label: 'No preference' },
  { value: 'male',   label: 'Male' },
  { value: 'female', label: 'Female' },
];

// ── tiny helpers ────────────────────────────────────────────────────────────────

const Chip = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '6px 13px',
      borderRadius: 20,
      fontSize: 13,
      border: selected ? '1.5px solid #166534' : '1px solid #d1d5db',
      background: selected ? '#dcfce7' : '#fff',
      color: selected ? '#14532d' : '#374151',
      cursor: 'pointer',
      fontWeight: selected ? 600 : 400,
      transition: 'all 0.15s',
      lineHeight: 1.4,
    }}
  >
    {label}
  </button>
);

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: '#6b7280', textTransform: 'uppercase', marginBottom: 8 }}>
    {children}
  </div>
);

const NavButton = ({ onClick, disabled, variant = 'primary', children }) => {
  const isPrimary = variant === 'primary';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1,
        padding: '11px 0',
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 600,
        border: isPrimary ? 'none' : '1px solid #d1d5db',
        background: isPrimary ? (disabled ? '#9ca3af' : '#166534') : '#f9fafb',
        color: isPrimary ? '#fff' : '#374151',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'background 0.15s',
      }}
    >
      {children}
    </button>
  );
};

// ── step dot indicator ──────────────────────────────────────────────────────────
const StepDots = ({ step }) => (
  <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
    {[1, 2, 3].map(n => (
      <div
        key={n}
        style={{
          width: n === step ? 18 : 7,
          height: 7,
          borderRadius: 4,
          background: n <= step ? '#166534' : '#d1d5db',
          transition: 'all 0.2s',
        }}
      />
    ))}
  </div>
);

// ── main component ──────────────────────────────────────────────────────────────
const AISpecialistMatcher = ({ specialists = [], onMatchResults, onClear, isActive }) => {
  const [isOpen, setIsOpen]         = useState(false);
  const [step, setStep]             = useState(1);   // 1 | 2 | 3 | 'loading' | 'done' | 'error'
  const [errorMsg, setErrorMsg]     = useState('');

  // Step 1
  const [symptoms, setSymptoms]       = useState([]);
  const [otherSymptom, setOtherSymptom] = useState('');
  const [flockTypes, setFlockTypes]   = useState([]);

  // Step 2
  const [budget, setBudget]           = useState(150);
  const [availability, setAvailability] = useState('available');
  const [gender, setGender]           = useState('any');

  // Step 3
  const [location, setLocation]       = useState('');
  const [extraContext, setExtraContext] = useState('');

  // Result
  const [resultSummary, setResultSummary] = useState('');
  const [matchCount, setMatchCount]   = useState(0);

  // Re-open restores panel without resetting answers
  const handleOpen  = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const toggleMulti = (val, arr, setArr) => {
    setArr(arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val]);
  };

  const handleReset = () => {
    setSymptoms([]);
    setOtherSymptom('');
    setFlockTypes([]);
    setBudget(150);
    setAvailability('available');
    setGender('any');
    setLocation('');
    setExtraContext('');
    setResultSummary('');
    setMatchCount(0);
    setErrorMsg('');
    setStep(1);
    onClear?.();
  };

  // ── build specialist context for the AI ──────────────────────────────────────
  const buildSpecialistContext = () =>
    specialists.map(s => ({
      id: s._id,
      name: s.name,
      specialties: s.specialties || [],
      bio: s.bio || '',
      availability: s.availability,
      fees: s.fees,
      country: s.country,
      state: s.state,
      experience: s.experience,
      gender: s.gender,
    }));

  // ── compose the human-readable query from the form ──────────────────────────
  const buildUserQuery = () => {
    const symptomLabels = symptoms.map(
      v => SYMPTOM_OPTIONS.find(o => o.value === v)?.label || v
    );
    if (symptoms.includes('other') && otherSymptom.trim()) {
      symptomLabels.push(otherSymptom.trim());
    }
    const flockLabels = flockTypes.map(
      v => FLOCK_OPTIONS.find(o => o.value === v)?.label || v
    );

    const lines = [
      symptomLabels.length
        ? `Symptoms / issues: ${symptomLabels.join(', ')}.`
        : 'No specific symptoms selected.',
      flockLabels.length
        ? `Flock type: ${flockLabels.join(', ')}.`
        : '',
      `Budget: up to £${budget} per consultation.`,
      `Availability required: ${availability === 'available' ? 'must be available now' : 'any availability'}.`,
      gender !== 'any' ? `Gender preference: ${gender}.` : '',
      location.trim() ? `Location: ${location.trim()}.` : '',
      extraContext.trim() ? `Additional context: ${extraContext.trim()}` : '',
    ].filter(Boolean);

    return lines.join('\n');
  };

  // ── send to AI ────────────────────────────────────────────────────────────────
  const handleFindSpecialists = async () => {
    setStep('loading');
    setErrorMsg('');

    const userQuery    = buildUserQuery();
    const specialistCtx = buildSpecialistContext();

    const systemPrompt = `You are an AI assistant for a UK poultry farming platform that matches farmers with veterinary specialists.

You will receive a structured description of the farmer's needs and a JSON list of available specialists.

Return ONLY a JSON object in this exact format (no markdown, no extra text):
{
  "matchedIds": ["id1", "id2", "id3"],
  "summary": "One sentence explaining why these specialists were chosen.",
  "reasoning": [
    { "id": "id1", "reason": "Short reason why this specialist fits" },
    { "id": "id2", "reason": "Short reason" }
  ]
}

Rules:
- matchedIds: array of _id strings from the specialists list, ranked best-first, max 6
- Match on: specialties relevant to the stated symptoms/flock type, availability, fees within budget, location if given, gender preference if stated
- If no good matches exist, return matchedIds: [] and explain in summary
- Do NOT invent or assume data not present in the specialist list

Available specialists:
${JSON.stringify(specialistCtx, null, 2)}`;

    try {
      const data = await apiService.post('/chat-ai', {
        model: 'gpt-4o-mini',
        max_tokens: 1000,
        system: systemPrompt,
        messages: [{ role: 'user', content: userQuery }],
      });

      const rawText = data?.choices?.[0]?.message?.content || '';
      const cleaned = rawText.replace(/```json|```/g, '').trim();
      const parsed  = JSON.parse(cleaned);

      const { matchedIds = [], summary = '', reasoning = [] } = parsed;
      const reasoningMap = {};
      reasoning.forEach(r => { reasoningMap[r.id] = r.reason; });

      setMatchCount(matchedIds.length);
      setResultSummary(summary);
      setStep('done');

      onMatchResults?.({ matchedIds, summary, reasoningMap });
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
      setStep('error');
    }
  };

  // ── summary text for step 3 preview ─────────────────────────────────────────
  const summaryLines = [
    symptoms.length
      ? `Symptoms: ${symptoms.map(v => SYMPTOM_OPTIONS.find(o => o.value === v)?.label || v).join(', ')}`
      : null,
    flockTypes.length
      ? `Flock: ${flockTypes.map(v => FLOCK_OPTIONS.find(o => o.value === v)?.label || v).join(', ')}`
      : null,
    `Budget: up to £${budget}`,
    `Availability: ${availability === 'available' ? 'Available now' : 'Any'}`,
    gender !== 'any' ? `Gender: ${gender}` : null,
    location.trim() ? `Location: ${location.trim()}` : null,
  ].filter(Boolean);

  // ── panel open state ─────────────────────────────────────────────────────────
  const panelHeight = isOpen ? 580 : 0;

  return (
    <>
      {/* ── Floating trigger ── */}
      <button
        onClick={handleOpen}
        title="AI Specialist Matcher"
        style={{
          position: 'fixed',
          bottom: 28,
          right: 28,
          zIndex: 1000,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: isActive ? '#14532d' : '#166534',
          border: isActive ? '2px solid #4ade80' : '2px solid transparent',
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(22,101,52,0.45)',
          fontSize: 22,
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        🤖
        {isActive && (
          <span style={{ position: 'absolute', top: 0, right: 0, width: 14, height: 14, background: '#4ade80', borderRadius: '50%', border: '2px solid #fff' }} />
        )}
      </button>

      {/* ── Backdrop ── */}
      {isOpen && (
        <div
          onClick={handleClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.22)', zIndex: 1001 }}
        />
      )}

      {/* ── Slide-up panel ── */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          width: '100%',
          maxWidth: 420,
          height: panelHeight,
          zIndex: 1002,
          borderRadius: '20px 20px 0 0',
          background: '#fff',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.13)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'height 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      >
        {/* ── Header ── */}
        <div style={{ padding: '14px 18px 12px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, background: '#f0fdf4' }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: '#166534', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🤖</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>AI Specialist Matcher</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>{specialists.length} specialists · Answer a few quick questions</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {typeof step === 'number' && <StepDots step={step} />}
            {(step === 'done' || isActive) && (
              <button onClick={handleReset} style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 7, padding: '4px 10px', fontSize: 11, color: '#92400e', cursor: 'pointer', fontWeight: 600 }}>↺ Reset</button>
            )}
            <button onClick={handleClose} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 7, padding: '4px 8px', fontSize: 13, color: '#6b7280', cursor: 'pointer' }}>✕</button>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 16px' }}>

          {/* ──── STEP 1 ──── */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <SectionLabel>What symptoms are you seeing? <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: '#9ca3af' }}>(select all that apply)</span></SectionLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {SYMPTOM_OPTIONS.map(o => (
                    <Chip key={o.value} label={o.label} selected={symptoms.includes(o.value)} onClick={() => toggleMulti(o.value, symptoms, setSymptoms)} />
                  ))}
                </div>
                {symptoms.includes('other') && (
                  <input
                    value={otherSymptom}
                    onChange={e => setOtherSymptom(e.target.value)}
                    placeholder="Describe your symptoms..."
                    style={{ marginTop: 10, width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', color: '#111827' }}
                  />
                )}
              </div>

              <div>
                <SectionLabel>Flock type <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: '#9ca3af' }}>(select all that apply)</span></SectionLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {FLOCK_OPTIONS.map(o => (
                    <Chip key={o.value} label={o.label} selected={flockTypes.includes(o.value)} onClick={() => toggleMulti(o.value, flockTypes, setFlockTypes)} />
                  ))}
                </div>
              </div>

              <NavButton onClick={() => setStep(2)}>Continue →</NavButton>
            </div>
          )}

          {/* ──── STEP 2 ──── */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <SectionLabel>Budget per consultation</SectionLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>£0</span>
                  <input
                    type="range" min={0} max={500} step={10} value={budget}
                    onChange={e => setBudget(Number(e.target.value))}
                    style={{ flex: 1, accentColor: '#166534' }}
                  />
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>£500+</span>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <span style={{ fontSize: 26, fontWeight: 700, color: '#166534' }}>£{budget}</span>
                  <span style={{ fontSize: 12, color: '#6b7280' }}> max</span>
                </div>
              </div>

              <div>
                <SectionLabel>Availability</SectionLabel>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[{ v: 'available', label: '● Available now' }, { v: 'any', label: '◌ Any availability' }].map(opt => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setAvailability(opt.v)}
                      style={{
                        flex: 1, padding: '9px 0', borderRadius: 9, fontSize: 13,
                        border: availability === opt.v ? '1.5px solid #166534' : '1px solid #d1d5db',
                        background: availability === opt.v ? '#dcfce7' : '#fff',
                        color: availability === opt.v ? '#14532d' : '#6b7280',
                        fontWeight: availability === opt.v ? 600 : 400,
                        cursor: 'pointer',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <SectionLabel>Specialist gender preference</SectionLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {GENDER_OPTIONS.map(o => (
                    <Chip key={o.value} label={o.label} selected={gender === o.value} onClick={() => setGender(o.value)} />
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <NavButton variant="secondary" onClick={() => setStep(1)}>← Back</NavButton>
                <NavButton onClick={() => setStep(3)}>Continue →</NavButton>
              </div>
            </div>
          )}

          {/* ──── STEP 3 ──── */}
          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <SectionLabel>Your location <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: '#9ca3af' }}>(optional)</span></SectionLabel>
                <input
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  placeholder="e.g. Yorkshire, UK"
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', color: '#111827' }}
                />
              </div>

              <div>
                <SectionLabel>Anything else to add? <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: '#9ca3af' }}>(optional)</span></SectionLabel>
                <textarea
                  rows={3}
                  value={extraContext}
                  onChange={e => setExtraContext(e.target.value)}
                  placeholder="e.g. flock size, urgency, prefer video call..."
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'none', outline: 'none', color: '#111827' }}
                />
              </div>

              {/* Summary preview */}
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: '#9ca3af', textTransform: 'uppercase', marginBottom: 6 }}>Your search</div>
                {summaryLines.length === 0
                  ? <span style={{ fontSize: 12, color: '#9ca3af' }}>No criteria selected yet.</span>
                  : summaryLines.map((line, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#374151', lineHeight: 1.7 }}>{line}</div>
                    ))
                }
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <NavButton variant="secondary" onClick={() => setStep(2)}>← Back</NavButton>
                <NavButton onClick={handleFindSpecialists} disabled={symptoms.length === 0 && flockTypes.length === 0}>
                  🤖 Find my specialists
                </NavButton>
              </div>
            </div>
          )}

          {/* ──── LOADING ──── */}
          {step === 'loading' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 16 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {[0, 0.2, 0.4].map((delay, i) => (
                  <span key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: '#166534', display: 'inline-block', animation: 'bounce 1.2s ease-in-out infinite', animationDelay: `${delay}s` }} />
                ))}
              </div>
              <span style={{ fontSize: 13, color: '#6b7280' }}>Matching you with specialists…</span>
            </div>
          )}

          {/* ──── DONE ──── */}
          {step === 'done' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#166534', marginBottom: 4 }}>
                  ✓ {matchCount} specialist{matchCount !== 1 ? 's' : ''} matched
                </div>
                {resultSummary && <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{resultSummary}</div>}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', lineHeight: 1.6 }}>
                Matched specialists are highlighted and ranked in the list above.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <NavButton variant="secondary" onClick={handleReset}>↺ New search</NavButton>
                <NavButton onClick={handleClose}>View results →</NavButton>
              </div>
            </div>
          )}

          {/* ──── ERROR ──── */}
          {step === 'error' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#991b1b' }}>
                {errorMsg || 'Something went wrong. Please try again.'}
              </div>
              <NavButton onClick={() => setStep(3)}>← Try again</NavButton>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-8px); }
        }
      `}</style>
    </>
  );
};

export default AISpecialistMatcher;